import { useRef } from 'react';
import { getSymbolForKey } from '../../lib/platform';

import { ToastManager } from '../../ToastManager';
import { userMeDraftStore } from '../../store/users/UserMeDraftStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useAdvancedFlow } from '../../store/advanced-flow/useAdvancedFlows';
import { useUserMe } from '../../store/users/useUserMe';
import { useApi } from '../../store/SelectedHomeyStore';

import { theme } from '../../theme/theme';

import { ContextMenuContent } from '../common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../common/context-menu/DeveloperMenu';

import { iconMouseLeftClick } from '../../theme/icons/interface/mouse-left-click';
import { iconNotFavorite } from '../../theme/icons/interface/heart-outline';
import { iconFavorite } from '../../theme/icons/interface/heart-fill';

export function AdvancedFlowContextMenuContent(props) {
  const { i18n } = useI18n();
  // const ctx = useContextMenuContext();

  const pendingRef = useRef({
    favorite: false,
  });
  const { api } = useApi();
  const { advancedFlow } = useAdvancedFlow({ advancedFlowId: props.advancedFlowId });
  const { userMe } = useUserMe();

  const isFavoriteFlow = userMe?.properties.favoriteFlows?.includes(props.advancedFlowId) ?? false;

  async function onAction(key) {
    switch (key) {
      case 'trigger':
        props.onTriggerRequest?.();
        break;
      case 'favorite':
        (async () => {
          const favoriteName = `**${advancedFlow.name}**`;

          if (pendingRef.current.favorite === true) {
            ToastManager.add({
              message: 'Request still pending, please wait...',
            });
            return;
          }

          if (isFavoriteFlow) {
            try {
              pendingRef.current.favorite = true;

              const nextUserMe = {
                ...userMe,
                properties: {
                  ...userMe.properties,
                  favoriteFlows: (userMe.properties.favoriteFlows ?? []).filter(
                    (id) => id !== props.advancedFlowId
                  ),
                },
              };
              userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

              await api.users.updateUserMeProperties({
                id: 'favoriteFlows',
                value: nextUserMe.properties.favoriteFlows,
              });

              ToastManager.add({
                icon: iconNotFavorite,
                iconColor: theme.icon.color_light,
                message: i18n.messageFormatter(`flow.removeFavorite`, {
                  name: favoriteName,
                }),
              });
              userMeDraftStore.setUserMeDraftDone();
            } catch (error) {
              userMeDraftStore.setUserMeDraftFailed();
              ToastManager.handleError(error);
            } finally {
              pendingRef.current.favorite = false;
            }

            return;
          }

          try {
            pendingRef.current.favorite = true;

            const nextUserMe = {
              ...userMe,
              properties: {
                ...userMe.properties,
                favoriteFlows: [...(userMe.properties.favoriteFlows ?? []), props.advancedFlowId],
              },
            };
            userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

            await api.users.updateUserMeProperties({
              id: 'favoriteFlows',
              value: nextUserMe.properties.favoriteFlows,
            });

            ToastManager.add({
              icon: iconFavorite,
              iconColor: theme.color.special_favorite,
              message: i18n.messageFormatter(`flow.addFavorite`, {
                name: favoriteName,
              }),
            });
            userMeDraftStore.setUserMeDraftDone();
          } catch (error) {
            userMeDraftStore.setUserMeDraftFailed();
            ToastManager.handleError(error);
          } finally {
            pendingRef.current.favorite = false;
          }
        })().catch(ToastManager.handleError);
        break;
      default:
        break;
    }
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`flow.start`)}
        isDisabled={advancedFlow?.triggerable !== true}
        hint={`${getSymbolForKey('alt')} +`}
        icon={iconMouseLeftClick}
        onPress={() => {
          onAction('trigger');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`flow.favorite`)}
        icon={isFavoriteFlow ? iconFavorite : iconNotFavorite}
        onPress={() => {
          onAction('favorite');
        }}
      />

      <DeveloperMenu onIdRequest={() => advancedFlow?.id} onResourceRequest={() => advancedFlow} />
    </ContextMenuContent.List>
  );
}
